import Big from 'big.js';
import {
  CurrencyValueResponse,
  ProjectedCostsResponse,
  RecommendedDirectDebitResponse,
} from '@/src/types/Response';

const nullSafeLength = (array: any[] | null) => {
  return array ? array.length : 0;
};

export const getChartData = (
  balance: CurrencyValueResponse,
  {
    electricity,
    gas,
    boltOns,
    recommendedDirectDebit: { remainingDirectDebits },
  }: ProjectedCostsResponse,
  newDirectDebit: CurrencyValueResponse,
) => {
  if (electricity == null && gas == null && boltOns == null) {
    return [];
  }

  const months = Math.max(
    nullSafeLength(electricity),
    nullSafeLength(gas),
    nullSafeLength(boltOns),
  );

  const totalCosts = [...Array(months)].map((_, i) => {
    const electricityData = electricity && electricity[i];
    const gasData = gas && gas[i];
    const boltOnData = boltOns && boltOns[i];

    return Big(electricityData ? electricityData?.cost?.amount : 0)
      .plus(gasData ? gasData?.cost?.amount : 0)
      .plus(boltOnData ? boltOnData?.cost?.amount : 0);
  });

  const firstMonthWithCostIndex = totalCosts.findIndex(e => !e.eq(0));

  const firstDataDate = (index: number) => {
    const fuelData =
      (electricity || []).length > (gas || []).length ? electricity : gas;

    const data =
      (boltOns || []).length > (fuelData || []).length ? boltOns : fuelData;

    return data![index].date;
  };

  return totalCosts
    .reduce<Array<{ date: string; balance: Big }>>((acc, cost, index) => {
      // skip months with no cost
      const date = firstDataDate(index);
      if (index < firstMonthWithCostIndex) {
        acc.push({ date, balance: Big(0) });
        return acc;
      }

      const startingBalance =
        index === firstMonthWithCostIndex ||
        (firstMonthWithCostIndex === -1 && index === 0)
          ? Big(balance ? balance?.amount : 0)
          : acc[index - 1]!.balance;

      const firstMonthWithDD =
        firstMonthWithCostIndex +
        (remainingDirectDebits.includesCurrentMonth ? 0 : 1);
      const lastMonthWithDD = firstMonthWithDD + remainingDirectDebits.count;

      const hasDirectDebit =
        index >= firstMonthWithDD && index < lastMonthWithDD;

      const monthBalance = startingBalance
        .plus(hasDirectDebit ? newDirectDebit?.amount : 0)
        .minus(cost);

      acc.push({ date, balance: monthBalance });
      return acc;
    }, [])
    .filter(e => !e.balance.eq(0));
};

export const getPredictableBalance = (
  recommendedDirectDebit: RecommendedDirectDebitResponse,
  newDirectDebit: CurrencyValueResponse,
  isStandardDDCustomer: boolean,
) => {
  const {
    balance,
    remainingDirectDebits,
    predictedBoltOnCost,
    predictedEnergyCost,
    yearlyPredictedEnergyCost,
  } = recommendedDirectDebit;

  // 1 month divided by 12 months in number form
  const oneMonth = 0.0833;

  const averageOneMonthConsumptionCost = Big(
    yearlyPredictedEnergyCost.amount,
  ).times(oneMonth);

  const standardDDCustomerEnergyCost = Big(predictedEnergyCost.amount).minus(
    averageOneMonthConsumptionCost,
  );

  const energyCost = isStandardDDCustomer
    ? standardDDCustomerEnergyCost
    : Big(predictedEnergyCost.amount);

  const predictableBalance = Big(balance.amount)
    .minus(energyCost)
    .minus(Big(predictedBoltOnCost.amount))
    .plus(Big(remainingDirectDebits.count).times(Big(newDirectDebit.amount)));

  return predictableBalance.round(0, 2);
};
